import { logAuthFlowInfo, logAuthFlowWarning } from "./authFlowLogger";
import { getResponseRequestId, isNetworkError } from "./authDiagnostics";
import { getApiBaseUrl } from "../runtime/apiBaseUrl";

export const BOOTSTRAP_REASON_CODES = Object.freeze({
    invalid: "bootstrap_invalid",
    expired: "bootstrap_expired",
    used: "bootstrap_used",
    wrongDestination: "bootstrap_wrong_destination",
    issueDenied: "bootstrap_issue_denied",
    issueFailed: "bootstrap_issue_failed",
    networkFailed: "bootstrap_network_failed",
    issueTimedOut: "bootstrap_issue_timeout",
    exchangeFailed: "bootstrap_exchange_failed",
});

const BOOTSTRAP_ISSUE_TIMEOUT_MS = 15000;
const BOOTSTRAP_ISSUE_RETRY_DELAYS_MS = Object.freeze([700, 1600]);

function wait(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function pickFirstValue(...values) {
    for (const value of values) {
        if (value === null || value === undefined) {
            continue;
        }

        if (typeof value === "string" && value.trim() === "") {
            continue;
        }

        return value;
    }

    return null;
}

function extractAccessIdentity(access) {
    const sharedAccessContext = access?.sharedAccessContext || null;
    const portalAccounts = Array.isArray(access?.portals)
        ? access.portals.map((entry) => entry?.access?.account || null).filter(Boolean)
        : [];
    const portalAccountWithEmail = portalAccounts.find((account) => account?.email);
    const portalAccountWithPhone = portalAccounts.find((account) => account?.phone || account?.authPhone);

    return {
        email: pickFirstValue(
            sharedAccessContext?.broker?.user?.email,
            sharedAccessContext?.client?.user?.email,
            sharedAccessContext?.investor?.user?.email,
            sharedAccessContext?.referrer?.user?.email,
            portalAccountWithEmail?.email,
        ),
        phone: pickFirstValue(
            sharedAccessContext?.broker?.user?.phone,
            sharedAccessContext?.client?.user?.phone,
            sharedAccessContext?.investor?.user?.phone,
            sharedAccessContext?.referrer?.user?.phone,
            sharedAccessContext?.application?.user?.authPhone,
            portalAccountWithPhone?.phone,
            portalAccountWithPhone?.authPhone,
        ),
    };
}

function isTimeoutError(error) {
    return /abort|timeout|timed out/i.test(`${error?.name || ""} ${error?.message || ""}`);
}

function buildNetworkBootstrapError(error) {
    const timedOut = isTimeoutError(error);
    const nextError = new Error(
        timedOut
            ? "The secure handoff request timed out before the workspace could be opened."
            : "We couldn't reach the secure handoff service. Check your connection and try again."
    );
    nextError.code = timedOut
        ? BOOTSTRAP_REASON_CODES.issueTimedOut
        : BOOTSTRAP_REASON_CODES.networkFailed;
    nextError.cause = error;
    return nextError;
}

async function fetchBootstrapIssue(body) {
    const hasAbortController = typeof AbortController !== "undefined";
    const controller = hasAbortController ? new AbortController() : null;
    const timeoutId = controller
        ? setTimeout(() => {
            controller.abort();
        }, BOOTSTRAP_ISSUE_TIMEOUT_MS)
        : null;

    try {
        const bootstrapIssueToken = String(body.bootstrapIssueToken || "").trim();
        const headers = { "Content-Type": "application/json" };
        if (bootstrapIssueToken) {
            headers["x-auth-session-token"] = bootstrapIssueToken;
        }

        const requestBody = { ...body };
        delete requestBody.bootstrapIssueToken;

        return await fetch(`${getApiBaseUrl()}/api/auth/portal-bootstrap/issue`, {
            method: "POST",
            credentials: "include",
            headers,
            signal: controller?.signal,
            body: JSON.stringify(requestBody),
        });
    } finally {
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
        }
    }
}

async function requestBootstrapIssueWithRetry(body, context) {
    let lastError = null;

    for (let attempt = 0; attempt <= BOOTSTRAP_ISSUE_RETRY_DELAYS_MS.length; attempt += 1) {
        try {
            return await fetchBootstrapIssue(body);
        } catch (error) {
            lastError = error;

            if (!isNetworkError(error) || attempt >= BOOTSTRAP_ISSUE_RETRY_DELAYS_MS.length) {
                break;
            }

            logAuthFlowWarning("bootstrap_issue_network_retry", {
                ...context,
                attempt: attempt + 1,
                message: error?.message || "network_error",
            });
            await wait(BOOTSTRAP_ISSUE_RETRY_DELAYS_MS[attempt]);
        }
    }

    throw buildNetworkBootstrapError(lastError);
}

export async function issuePortalBootstrap({
    session,
    portal,
    requestedIntent,
    hasExplicitIntent,
    source,
    returnTo,
    rememberDevice = false,
    verifiedEmail = null,
    verifiedPhone = null,
    authUserId: explicitAuthUserId = null,
}) {
    const user = session?.user || {};
    const identity = session?.identity || {};
    const authSession = session?.session || {};
    const accessIdentity = extractAccessIdentity(session?.access);
    const bootstrapIssueToken = pickFirstValue(
        authSession?.bootstrapIssueToken,
        session?.bootstrapIssueToken,
    );
    const phone = pickFirstValue(
        verifiedPhone,
        user?.phone,
        identity?.phone?.phoneDigits,
        identity?.phone?.phoneE164,
        accessIdentity.phone,
    );
    const email = pickFirstValue(
        verifiedEmail,
        user?.email,
        identity?.email,
        accessIdentity.email,
    );
    const authUserId = pickFirstValue(
        explicitAuthUserId,
        user?.id,
        identity?.identityId,
        authSession?.identityId,
        authSession?.sessionId,
    );

    logAuthFlowInfo("bootstrap_issue_started", {
        portalId: portal.portalId,
        requestedIntent,
        source,
        authUserId,
        phone,
        email,
    });

    const requestBody = {
        portalId: portal.portalId,
        authUserId,
        phone,
        email,
        intent: requestedIntent,
        hasExplicitIntent,
        source,
        returnTo,
        rememberDevice,
        bootstrapIssueToken,
    };
    const response = await requestBootstrapIssueWithRetry(requestBody, {
        portalId: portal.portalId,
        requestedIntent,
        source,
    });

    const payload = await response.json().catch(() => ({}));
    const requestId = getResponseRequestId(response, payload);

    if (!response.ok || !payload?.bootstrap?.bootstrapCode) {
        logAuthFlowWarning("bootstrap_issue_failed", {
            portalId: portal.portalId,
            requestedIntent,
            source,
            statusCode: response.status,
            reasonCode: payload?.code || payload?.bootstrap?.code || null,
            requestId,
        });
        const error = new Error(payload?.message || "We couldn't open your workspace right now.");
        error.status = response.status;
        error.code = payload?.code || BOOTSTRAP_REASON_CODES.issueFailed;
        error.body = payload;
        error.requestId = requestId;
        throw error;
    }

    logAuthFlowInfo("bootstrap_issued", {
        portalId: portal.portalId,
        requestedIntent,
        expiresAt: payload.bootstrap.expiresAt || null,
        requestId,
    });

    return {
        ...payload.bootstrap,
        requestId,
    };
}

export function buildBootstrapDestinationUrl(destinationUrl, bootstrapCode, returnTo, portalId = null) {
    const url = new URL(destinationUrl);
    url.searchParams.set("bootstrap", bootstrapCode);

    if (portalId) {
        url.searchParams.set("intent", portalId);
    }

    if (returnTo) {
        url.searchParams.set("returnTo", returnTo);
    }

    return url.toString();
}
